import React, { ReactElement, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ModalTypes } from '../../../enums/modal-types.enum';
import { ModalInterface } from '../../../interfaces/modal.interface';
import EmailsService from '../../../services/emails/emails.service';
import { setIsShowStudioMigrationModal } from '../../../store/slices/modals.slice';
import { selectUser } from '../../../store/slices/user.slice';
import Button from '../../Atoms/Button/Button';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { STUDIO_MIGRATION_COPY } from './StudioMigrationModal.const';
import styles from './StudioMigrationModal.module.scss';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const StudioMigrationModal = ({ isOpen }: ModalInterface): ReactElement => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser, shallowEqual);

  const [unlimitedEmail, setUnlimitedEmail] = useState('');
  const [migrateFiles, setMigrateFiles] = useState(false);
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setUnlimitedEmail(user?.email || '');
      setMigrateFiles(false);
      setComments('');
      setIsSubmitting(false);
      setIsSubmitted(false);
      setError('');
      TagManager.dataLayer({ dataLayer: { event: 'studioMigrationModalShown' } });
    }
  }, [isOpen, user]);

  const closeModal = (): void => {
    dispatch(setIsShowStudioMigrationModal(false));
  };

  const isEmailValid = EMAIL_REGEX.test(unlimitedEmail.trim());

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!isEmailValid || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await EmailsService.studioMigrationRequest({
        unlimitedEmail: unlimitedEmail.trim(),
        migrateFiles,
        migrateCredits: true,
        request: 'Account migration to Unlimited (Studio sunset).',
        comments
      });
      TagManager.dataLayer({ dataLayer: { event: 'studioMigrationRequested' } });
      setIsSubmitted(true);
    } catch (e) {
      console.error(e);
      setError(STUDIO_MIGRATION_COPY.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper isOpen={isOpen} onRequestClose={closeModal} modalType={ModalTypes.DEFAULT} className={styles.modal}>
      {isSubmitted ? (
        <div className={styles.content}>
          <h3 className={styles.title}>{STUDIO_MIGRATION_COPY.title}</h3>
          <p className={styles.success}>{STUDIO_MIGRATION_COPY.success}</p>
          <Button className={styles.submit} value="Close" onClick={closeModal} />
        </div>
      ) : (
        <form className={styles.content} onSubmit={onSubmit}>
          <h3 className={styles.title}>{STUDIO_MIGRATION_COPY.title}</h3>
          <p className={styles.subtitle}>{STUDIO_MIGRATION_COPY.subtitle}</p>

          <label className={styles.field}>
            <span className={styles.label}>{STUDIO_MIGRATION_COPY.emailLabel}</span>
            <input
              className={styles.input}
              type="email"
              value={unlimitedEmail}
              placeholder={STUDIO_MIGRATION_COPY.emailPlaceholder}
              onChange={e => setUnlimitedEmail(e.target.value)}
            />
          </label>

          <label className={styles.checkbox}>
            <input type="checkbox" checked={migrateFiles} onChange={e => setMigrateFiles(e.target.checked)} />
            <span>{STUDIO_MIGRATION_COPY.filesCheckbox}</span>
          </label>

          <p className={styles.warning}>{STUDIO_MIGRATION_COPY.warning}</p>

          <label className={styles.field}>
            <span className={styles.label}>{STUDIO_MIGRATION_COPY.commentsLabel}</span>
            <textarea
              className={styles.textarea}
              value={comments}
              rows={2}
              placeholder={STUDIO_MIGRATION_COPY.commentsPlaceholder}
              onChange={e => setComments(e.target.value)}
            />
          </label>

          {error && <p className={styles.error}>{error}</p>}

          <Button
            type="submit"
            className={styles.submit}
            value={isSubmitting ? STUDIO_MIGRATION_COPY.submittingLabel : STUDIO_MIGRATION_COPY.submitLabel}
            disabled={!isEmailValid || isSubmitting}
          />
        </form>
      )}
    </ModalWrapper>
  );
};

export default StudioMigrationModal;
